// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {filter, get, includes, isEmpty} from 'lodash';

// Redux Actions
import {
    refreshServicesData,
    resetServiceData,
    resetSort,
    servicesDataSearch,
    servicesRegisterStore,
    servicesUnregisterStore,
    setColorFilter,
    setFilter,
    setQuery,
    setSort,
} from '../actions/services-data-actions';
import {resetForm, setFieldValue} from '../actions/form-data-actions';

// Contexts
import {SearchContext} from '../contexts/search-context';

// Configs
import servicesConfig from '../../config/services-config';

const withSearch = (WrappedComponent, servicesDataKey) => {
    class WithSearch extends Component {
        componentDidMount() {
            const {colorFilterSet, setColorFilter, servicesRegisterStore} = this.props;
            servicesRegisterStore(servicesDataKey);
            if (colorFilterSet && colorFilterSet[servicesDataKey]) {
                setColorFilter(servicesDataKey, colorFilterSet[servicesDataKey]);
            } else {
                setColorFilter(servicesDataKey, null);
            }
        }

        getComponentProps() {
            const {
                dashboardProps,
                dependentFormData,
                dependentSearchData,
                routerParams,
                servicesDataSearch,
                servicesFormData,
                servicesSearchData,
                username,
            } = this.props;
            const {error, fetched, fetching, filter, query, searchData} = servicesSearchData;
            const {fetching: dependentFetching, searchData: dependentSearchValues} = dependentSearchData || {};
            const {values: formValues} = dependentFormData || {};

            return {
                ...this.props,
                activeRowKeys: get(servicesConfig, [servicesDataKey, 'activeRowKeys']),
                dashboardProps,
                dependentSearchValues,
                error,
                fetched,
                fetching: fetching || dependentFetching,
                formValues,
                number: get(searchData, 'number', 0),
                numberOfElements: get(searchData, 'numberOfElements', 0),
                query,
                resetServiceData: (actionShouldFetch) => this.resetServiceData(actionShouldFetch),
                routerParams,
                searchData: this.getSearchData(searchData, filter),
                searchFilter: filter,
                servicesDataKey,
                servicesDataSearch: () => servicesDataSearch(servicesDataKey),
                servicesFormData,
                size: get(searchData, 'size', 0),
                sort: get(query, 'sort', []),
                totalElements: get(searchData, 'totalElements', 0),
                totalPages: get(searchData, 'totalPages', 0),
                username,
            };
        }

        getSearchData(searchData, searchFilter = {}) {
            const {targets, activeId} = searchFilter;
            let content = get(searchData, 'content', []);

            if (!isEmpty(targets) && activeId) {
                content = filter(content, (record) => {
                    const fieldValue = get(record, activeId);

                    return fieldValue && includes(targets, fieldValue);
                });
            }

            return content;
        }

        resetServiceData = (actionShouldFetch = true) => {
            const {resetForm, resetServiceData} = this.props;

            return Promise.all([
                resetForm(servicesDataKey, actionShouldFetch),
                resetServiceData(servicesDataKey, actionShouldFetch),
            ]);
        };

        render() {
            const {
                refreshServicesData,
                resetForm,
                resetSort,
                servicesDataSearch,
                servicesSearchData,
                servicesUnregisterStore,
                setQuery,
                setSort,
            } = this.props;

            return !isEmpty(servicesSearchData) ? (
                <SearchContext.Provider
                    value={{
                        refreshServicesData: (servicesDataKeyOption) =>
                            refreshServicesData(servicesDataKeyOption || servicesDataKey),
                        resetServiceData: (actionShouldFetch) => this.resetServiceData(actionShouldFetch),
                        resetSort: (clearSorts, actionShouldFetch) =>
                            resetSort(servicesDataKey, clearSorts, actionShouldFetch),
                        servicesDataKey,
                        servicesDataSearch: () => servicesDataSearch(servicesDataKey),
                        servicesUnregisterStore: () => servicesUnregisterStore(servicesDataKey),
                        setQuery: (query) => setQuery(servicesDataKey, query),
                        setSort: (sortColumn) => setSort(servicesDataKey, sortColumn),
                        resetForm: (submitted, resetValues, editing) =>
                            resetForm(servicesDataKey, submitted, resetValues, editing),
                    }}
                >
                    <WrappedComponent {...this.getComponentProps()} />
                </SearchContext.Provider>
            ) : null;
        }
    }

    WithSearch.propTypes = {
        activeRowValue: PropTypes.string,
        additionalProps: PropTypes.object,
        colorFilterSet: PropTypes.object,
        dashboardProps: PropTypes.object,
        dependentFormData: PropTypes.object,
        dependentSearchData: PropTypes.object,
        refreshServicesData: PropTypes.func.isRequired,
        resetForm: PropTypes.func.isRequired,
        resetServiceData: PropTypes.func.isRequired,
        resetSort: PropTypes.func.isRequired,
        routerParams: PropTypes.object,
        servicesDataSearch: PropTypes.func.isRequired,
        servicesFormData: PropTypes.object,
        servicesRegisterStore: PropTypes.func.isRequired,
        servicesSearchData: PropTypes.object.isRequired,
        servicesUnregisterStore: PropTypes.func.isRequired,
        setColorFilter: PropTypes.func.isRequired,
        setQuery: PropTypes.func.isRequired,
        setSort: PropTypes.func.isRequired,
        username: PropTypes.string,
    };

    WithSearch.displayName = `WithSearch(${getDisplayName(WrappedComponent)})`;

    const mapStateToProps = ({auth: {user} = {}, formData, router, servicesSearchData}) => {
        const providerServiceDataKey = get(servicesConfig, [servicesDataKey, 'providerServiceDataKey']);
        const formName = get(servicesConfig, [servicesDataKey, 'formName']);

        return {
            providerServiceDataKey,
            dependentSearchData: providerServiceDataKey
                ? get(servicesSearchData, providerServiceDataKey) || {}
                : undefined,
            formName,
            dependentFormData: formName ? get(formData, formName) || {} : undefined,
            routerParams: get(router, 'routerParams') || {},
            servicesFormData: get(formData, servicesDataKey) || {},
            servicesSearchData: get(servicesSearchData, servicesDataKey) || {},
            colorFilterSet: get(servicesSearchData, 'currentColorFilter') || {},
            username: user,
        };
    };

    const mapDispatchToProps = {
        refreshServicesData,
        resetForm,
        resetServiceData,
        resetSort,
        servicesDataSearch,
        servicesRegisterStore,
        servicesUnregisterStore,
        setColorFilter,
        setFieldValue,
        setFilter,
        setQuery,
        setSort,
    };

    return connect(mapStateToProps, mapDispatchToProps)(WithSearch);
};

function getDisplayName(WrappedComponent) {
    return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

export default withSearch;
